import React, { useCallback, useState } from 'react';
import { IconButton } from '@mui/material';
import PropTypes from 'prop-types';
import { ReactComponent as InfoOutlinedIcon } from '../../../../assets/Icons/lucideIcons/info.svg';

import AttributeInfoPopover from './AttributeInfoPopover';

/**
 * InfoIconButton component for displaying attribute information
 * @param {Object} props - Component props
 * @param {Object} props.attribute - The attribute data
 * @param {Object} props.attributeSourceMapping - Mapping of attribute sources
 * @param {boolean} props.loading - Whether data is loading
 * @returns {React.ReactElement} The InfoIconButton component
 */
const InfoIconButton = ({ attribute, attributeSourceMapping, loading }) => {
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const isInfoPopoverOpen = Boolean(infoAnchorEl);

  // Handle info popover open
  const handleInfoClick = useCallback(
    (event) => {
      event.stopPropagation();
      setInfoAnchorEl(event.currentTarget);
    },
    [setInfoAnchorEl]
  );

  // Handle info popover close
  const handleInfoClose = useCallback(() => {
    setInfoAnchorEl(null);
  }, [setInfoAnchorEl]);

  return (
    <>
      <IconButton
        color="primary"
        title="Info"
        onClick={handleInfoClick}
        size="small"
      >
        <InfoOutlinedIcon width={18} height={18} />
      </IconButton>
      {isInfoPopoverOpen && (
        <AttributeInfoPopover
          open={isInfoPopoverOpen}
          anchorEl={infoAnchorEl}
          onClose={handleInfoClose}
          attribute={attribute}
          attributeSourceMapping={attributeSourceMapping}
          loading={loading}
        />
      )}
    </>
  );
};

InfoIconButton.propTypes = {
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data_type: PropTypes.string,
    attribute_origin: PropTypes.string,
  }),
  attributeSourceMapping: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  loading: PropTypes.bool,
};

export default InfoIconButton;
